import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { globalDialogAtom } from 'shared/ui/GlobalDialog'
import { useNodesOfTypeFromSource } from './hooks'
import { Box, Button, MenuItem, Select } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { html as beautifyHtml } from 'js-beautify'
import { PotentiallyHtml } from '../../components/PotentiallyHtml'
import { Table } from 'shared/ui/Table/Table'
import { IndexControl } from './IndexControl'

export function DataPreview(params: {
  configKey: string
  attributeKey: string
}) {
  const setDialogContent = useSetAtom(globalDialogAtom)
  const sourceData = useNodesOfTypeFromSource(params.configKey).map(
    ({ data, title }) => ({
      title,
      data: data[params.attributeKey],
    }),
  )

  if (!sourceData.length) return null

  const { data: firstItem } = sourceData[0]

  const shortPreview =
    ['string', 'number'].includes(typeof firstItem) &&
    String(firstItem).length < 20
  const buttonTitle = shortPreview ? String(firstItem) : 'Preview'

  const showPreview = () => {
    setDialogContent({
      title: `Preview of ${params.configKey}: ${params.attributeKey}`,
      contents: [<MultiDataItemPreview key="mdip" sourceData={sourceData} />],
    })
  }

  return <Button onClick={showPreview}>{buttonTitle}</Button>
}

function MultiDataItemPreview({
  sourceData,
}: {
  sourceData: { title: string; data: unknown }[]
}) {
  const [currentIndex, setCurrentIndex] = useState(0)

  return (
    <Box>
      <Box display="flex" justifyContent="space-between">
        <Select
          value={currentIndex}
          onChange={({ target: { value } }) => setCurrentIndex(Number(value))}
        >
          {sourceData.map(({ title }, i) => (
            <MenuItem value={i} key={i}>
              {title}
            </MenuItem>
          ))}
        </Select>
        <IndexControl
          onIndexUpdate={setCurrentIndex}
          currentIndex={currentIndex}
          total={sourceData.length}
        />
      </Box>
      <DataFullPreview dataItem={sourceData[currentIndex].data} />
    </Box>
  )
}

function DataFullPreview({ dataItem }: { dataItem: unknown }) {
  if (typeof dataItem === 'string') {
    return <StringPreview item={dataItem} />
  }

  if (Array.isArray(dataItem)) {
    return <ArrayPreview stuff={dataItem} />
  }

  if (dataItem && typeof dataItem === 'object') {
    const converted = Object.entries(dataItem).map(([key, value]) => {
      return {
        KEY: key,
        ...(value as object),
      }
    })
    return DataFullPreview({ dataItem: converted })
  }

  return 'I dont know'
}

function StringPreview({ item }: { item: string }) {
  const [asHtml, setAsHtml] = useState(true)

  const formatted = beautifyHtml(item, { indent_size: 2, wrap_line_length: 80 })

  return (
    <Box>
      <Box>
        <Button onClick={() => setAsHtml(!asHtml)}>
          {asHtml ? 'Raw String' : 'As HTML'}
        </Button>
      </Box>
      {asHtml && <PotentiallyHtml s={item} />}
      {!asHtml && (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {formatted}
        </pre>
      )}
    </Box>
  )
}

function ArrayPreview({ stuff }: { stuff: unknown[] }) {
  const itemTypes = stuff.reduce<string[]>((acc, item) => {
    const itemType = Array.isArray(item) ? 'array' : typeof item
    if (!acc.includes(itemType)) {
      acc.push(itemType)
    }
    return acc
  }, [])

  if (itemTypes.includes('object') || !stuff.length) {
    return <ObjectListPreview items={stuff as Record<string, unknown>[]} />
  }

  const asStrings = stuff.map((item) => {
    const itemType = Array.isArray(item) ? 'array' : typeof item
    if (['string', 'number'].includes(itemType)) {
      return String(item)
    }
    if (Array.isArray(item)) {
      return `[ "${item.join('", "')}" ]`
    }
    return String(item)
  })

  return (
    <Box>
      <Box>Array of {itemTypes.join(', ')}</Box>
      <Box>
        {asStrings.map((s, index) => (
          <Box padding="5px" key={index}>
            {s}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function ObjectListPreview({ items }: { items: Record<string, unknown>[] }) {
  const cols = useMemo(() => {
    return items
      .reduce<string[]>((acc, item) => {
        Object.keys(item).forEach((key) => {
          if (!acc.includes(key)) {
            acc.push(key)
          }
        })
        return acc
      }, [])
      .map((key) => {
        return {
          title: key,
          rowContent(param: Record<string, unknown>) {
            const content = String(param[key])
            if (content.length < 100) {
              return <PotentiallyHtml s={String(param[key])} />
            }
            return (
              <Button
                onClick={() => {
                  const blob = new Blob([content], { type: 'text/html' })
                  const url = URL.createObjectURL(blob)
                  window.open(url, '_blank')
                }}
              >
                Open
              </Button>
            )
          },
        }
      })
  }, [items])

  return <Table<Record<string, unknown>> columns={cols} items={items} />
}
